import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';

/**
 * Formly field type that renders a horizontal range slider with an inline value display.
 *
 * Props:
 * - `min`        – number   Minimum value (default: 0)
 * - `max`        – number   Maximum value (default: 100)
 * - `step`       – number   Step increment (default: 1)
 * - `unit`       – string   Unit suffix appended to the displayed value (e.g. `'pt'`, `'mm'`)
 * - `displayFn`  – function Optional transform applied to the current value for display only.
 *                           When provided, `unit` is ignored for display.
 *                           e.g. `(v) => Math.round(v / labelWidth * 100) + '%'`
 * - `label`      – string   Standard Formly label (used as aria-label for accessibility)
 *
 * Usage:
 * ```typescript
 * {
 *   key: 'fontSize',
 *   type: 'slider',
 *   props: { min: 5, max: 24, step: 1, unit: 'pt', label: 'Font Size' },
 * }
 * ```
 */
@Component({
  selector: 'app-slider-formly-type',
  standalone: true,
  imports: [],
  templateUrl: './slider-formly-type.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './slider-formly-type.component.css',
})
export class SliderFormlyTypeComponent extends FieldType<FieldTypeConfig> {
  get min(): number {
    return (this.props['min'] as number) ?? 0;
  }

  get max(): number {
    return (this.props['max'] as number) ?? 100;
  }

  get step(): number {
    return (this.props['step'] as number) ?? 1;
  }

  get unit(): string {
    return (this.props['unit'] as string) ?? '';
  }

  get currentValue(): number {
    return this.formControl.value ?? this.min;
  }

  get displayValue(): string {
    const fn = this.props['displayFn'] as ((v: number) => string) | undefined;
    if (fn) return fn(this.currentValue);
    return `${this.currentValue}${this.unit}`;
  }

  onInput(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.formControl.setValue(value);
    this.formControl.markAsDirty();
  }
}
